import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkOther, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  AUCTION_MARKET_URL,
  INSTANT_BUY_URL,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useHasInstantBuyCards } from 'hooks/useHasInstantBuyCards';

import { PromotionalEvent_PromotionalEvent } from './__generated__/index.graphql';

const Background = styled(SBHorizontal).attrs({ center: true })`
  padding: var(--unit);
  position: relative;
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.9)),
    var(--background) no-repeat center / cover;
  flex: 1;
  border-radius: var(--unit);
`;
const Logo = styled.img`
  height: 64px;
`;
type Props = {
  promotionalEvent: PromotionalEvent_PromotionalEvent;
};

export const PromotionalEvent = ({ promotionalEvent }: Props) => {
  const { sport } = useSportContext();

  const { pictureUrl, logoUrl, blogUrl, slug, algoliaFilters } =
    promotionalEvent;
  const instantBuyHasResults = useHasInstantBuyCards(sport, algoliaFilters);

  if (!sport) {
    return null;
  }

  const tab = instantBuyHasResults
    ? INSTANT_BUY_URL[sport]
    : AUCTION_MARKET_URL[sport];

  return (
    <Background
      style={
        {
          '--background': `url(${pictureUrl})`,
        } as React.CSSProperties
      }
    >
      <Horizontal>
        {logoUrl && <Logo src={logoUrl} alt="" />}
        <Vertical>
          <div>
            <Chip color="yellow">
              <FontAwesomeIcon icon={faClock} />
              <TimeLeft time={promotionalEvent.endDate} />
            </Chip>
          </div>
          <HeadlineXS>{promotionalEvent.title}</HeadlineXS>
        </Vertical>
      </Horizontal>

      <Horizontal>
        <LinkOverlay
          as={Button}
          size="small"
          color="tertiary"
          to={generatePath(
            `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`
          )}
        >
          <FormattedMessage {...glossary.buy} />
        </LinkOverlay>
        {blogUrl && (
          <LinkOther
            as={IconButton}
            href={blogUrl}
            color="quaternary"
            icon={faInfoCircle}
            small
          />
        )}
      </Horizontal>
    </Background>
  );
};

PromotionalEvent.fragments = {
  PromotionalEvent: gql`
    fragment PromotionalEvent_PromotionalEvent on PromotionalEventInterface {
      slug
      title
      pictureUrl
      logoUrl
      blogUrl
      endDate
      algoliaFilters
    }
  ` as TypedDocumentNode<PromotionalEvent_PromotionalEvent>,
};
